import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import ChatImages from '@components/Chat/ChatImages';
import DocumentCard from '@components/Chat/DocumentCard';
import MessageParts from '@components/Chat/MessageParts';
import TagsCollection from '@components/Chat/TagsCollection';
import { VoiceCard } from '@components/Chat/VoiceCard';
import Highlighter from '@components/HighlightWords';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { MediaViewer } from '@components/shared/MediaViewer.web';
import { Document, Chat, LocalFile, Message } from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useChatAvatar from '@hooks/useChatAvatar';
import { useChatTutorialWeb } from '@hooks/useChatTutorialWeb';
import useMe from '@hooks/useMe';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import theme from '@themes/theme';
import { openInNewTab } from '@utils/fileUtils';
import { triggersConfigPlain } from '@utils/mentions';

import { getMessageBody, useChatCardData } from './useChatCardData';

type ChatCardProps = {
  messageData?: Message[];
  chatCard: Chat;
  filterVal?: string;
  onPress?: (chat: Chat, messageId?: string) => void;
  onMutePress?: (chat: Chat) => void;
  onPinPress?: (chat: Chat) => void;
  isEditing: boolean;
  isSelected?: boolean;
  /** Highlights a chat whose member the Owner invited during onboarding. */
  isInvited?: boolean;
  highlightedBody?: string;
  highlightedTitle?: string;
  collapsed?: boolean;
  onRightClick?: (chatCard: Chat, mouseX: number, mouseY: number) => void;
  contentHeightLimit?: boolean;
  isFromChatSearch?: boolean;
};

const ChatCard: React.FC<ChatCardProps> = ({
  messageData,
  chatCard: chat,
  filterVal = '',
  onPress,
  onMutePress,
  onPinPress,
  isEditing,
  isSelected,
  isInvited = false,
  highlightedBody,
  highlightedTitle,
  collapsed = false,
  onRightClick,
  contentHeightLimit,
  isFromChatSearch = false,
}) => {
  const { t } = useTranslation('format');
  const {
    id: chatId,
    name,
    settingsPin,
    settingsMute,
    messageBody,
    recentActivityAt,
    messageIcon,
    isGroupChat,
  } = chat;
  const { activeChat } = useActiveChat();
  const unreadCount = chat?.unreadCount || 0;
  const [bodyWidth, setBodyWidth] = useState(0);
  const { getChatAvatarLabel } = useChatAvatar();
  const { setPreviewDocuments, setActiveIndex, setTagsCollections } =
    usePreviewDocuments();
  const [isHovered, setIsHovered] = useState(false);
  const [imageCarouselVisible, setImageCarouselVisible] = useState(false);
  const { me } = useMe();

  const {
    audioAttachments,
    imageAttachments,
    fileAttachments,
    hideIcon,
    tagsByAuthorCollections,
    isSelfChat,
    isPendingInviteChat,
    isSenderFriendRequest,
  } = useChatCardData({
    chat,
    messageData,
    me,
    isGroupChat,
    isFromChatSearch,
  });

  const selfChatAvatarType = isSelfChat ? 'user' : 'chat';
  const selfChatAvatarId = isSelfChat ? me?.id : chatId;

  const mainRef = useRef<HTMLElement>(null);
  const { isFromChatList, chatTutorialWebState } = useChatTutorialWeb();

  // TA-10879: memoized so the contextmenu effect below does not detach and
  // reattach a DOM listener on every render of every row.
  const handleContextMenu = useCallback(
    (e: { preventDefault: () => void; pageX: any; pageY: any }) => {
      e.preventDefault();
      onRightClick?.(chat, e.pageX, e.pageY);
    },
    [chat, onRightClick]
  );

  const isChatPendingRequest =
    chat.isPendingRequest && !chat.userIds.some((userId) => userId === me?.id);
  useEffect(() => {
    const element = mainRef.current;
    if (element) {
      element.addEventListener('contextmenu', handleContextMenu);
      return () => {
        element.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, [handleContextMenu]);

  const messageContent = (
    <Box>
      {audioAttachments?.map((document) => (
        <Box marginBottom='xs' key={document?.id}>
          <VoiceCard
            onLongPress={() => {
              void 0;
            }}
            key={document?.id}
            voice={document}
          />
        </Box>
      ))}
      {fileAttachments?.map((document) => (
        <Box marginBottom='xxs' key={document?.id}>
          <DocumentCard
            filterVal={filterVal}
            key={document?.id}
            document={document}
            onPress={() => openInNewTab(document)}
            onLongPress={() => {
              void 0;
            }}
          />
        </Box>
      ))}

      {imageAttachments?.length > 0 && (
        <Box marginBottom='xxs'>
          <ChatImages
            isForwarded={false}
            onPress={(image: Document | LocalFile) => {
              setPreviewDocuments([...imageAttachments]);
              setActiveIndex(
                imageAttachments.findIndex((i) => i?.id === image?.id)
              );
              setTagsCollections(tagsByAuthorCollections ?? []);
              setImageCarouselVisible(true);
            }}
            onLongPress={() => {
              void 0;
            }}
            list={imageAttachments}
          />
        </Box>
      )}

      {tagsByAuthorCollections?.map((tagCollections) => {
        return tagCollections?.map((item) => {
          const { project, tasks, author } = item;
          return (
            <Box marginBottom='xs' key={project.id}>
              <TagsCollection
                filterVal={filterVal}
                onLongPress={() => void 0}
                tagsCollection={{ project, tasks, author }}
                showAuthor={false}
              />
            </Box>
          );
        });
      })}
    </Box>
  );

  const highlightText = (body: string) => {
    if (!filterVal) {
      return body;
    }
    return (
      <Highlighter
        autoEscape
        highlightStyle={{
          backgroundColor: theme.colors.yellowBright,
        }}
        searchWords={filterVal.split(' ') || ''}
        textToHighlight={body ?? ''}
      />
    );
  };

  const backgroundColor = isHovered ? theme.colors.grey01 : 'white';
  const chatSubtitleColor =
    isPendingInviteChat || isChatPendingRequest ? 'grey04' : 'textSecondary';
  const chatSubtitleStyle = isPendingInviteChat
    ? { fontStyle: 'italic' as const }
    : {};
  let chatSubtitleContent: React.ReactNode;
  if (isPendingInviteChat) {
    chatSubtitleContent = t('models:chat.pendingSubtitle');
  } else if (
    chat.isPendingRequest &&
    chat.ownerId !== me?.id &&
    chat.canCurrentUserDecide === false &&
    !chat.userIds?.some((u) => u === me?.id)
  ) {
    chatSubtitleContent = t('models:chat.askToJoinLogisticTeam');
  } else if (
    chat.isPendingRequest &&
    chat.ownerId !== me?.id &&
    chat.canCurrentUserDecide === true &&
    !chat.userIds?.some((u) => u === me?.id)
  ) {
    chatSubtitleContent = t('models:chat.YouVeBeenInvitedToJoinThisGroup');
  } else {
    chatSubtitleContent = (
      <MessageParts
        value={messageBody || ''}
        configs={[triggersConfigPlain.mention]}
      />
    );
  }

  const chatTitle = name
    ? highlightText(name)
    : highlightText(getChatAvatarLabel(chat));

  const chatAvatar = () => {
    if (isChatPendingRequest) {
      return (
        <Avatar
          avatarType={selfChatAvatarType}
          backgroundColor={theme.colors.grey02}
          id={selfChatAvatarId}
          avatarUrl={chat.avatarUrl}
          label={name ? name : getChatAvatarLabel(chat)}
          skipCache={true}
          isMultiple={isGroupChat}
          disableAvatarPress={true}
          // isSenderFriendRequest={true}
          groupColorSeed={isGroupChat ? name : undefined}
        />
      );
    }
    return isPendingInviteChat ? (
      <EmailAvatar width={48} />
    ) : (
      <Avatar
        avatarType={selfChatAvatarType}
        id={selfChatAvatarId}
        avatarUrl={chat.avatarUrl}
        label={name ? name : getChatAvatarLabel(chat)}
        skipCache={true}
        isMultiple={isGroupChat}
        disableAvatarPress
        isSenderFriendRequest={isSenderFriendRequest}
        groupColorSeed={isGroupChat ? name : undefined}
      />
    );
  };
  return (
    <>
      {imageCarouselVisible && (
        <MediaViewer
          chatId={chatId}
          messageId={messageData?.[0]?.id}
          onDismiss={() => setImageCarouselVisible(false)}
        />
      )}
      <Box
        bg='white'
        ref={mainRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor:
            activeChat?.id == chatId || isInvited
              ? theme.colors.greenSecondaryMild
              : backgroundColor,
        }}>
        <TouchableOpacity
          key={chatId}
          onPress={() => {
            setIsHovered(false);
            const message = messageData?.[0];
            const { id: messageId } = message || {};

            onPress && onPress(chat, messageId);
          }}
          style={[
            styles.chatCard,
            {
              paddingHorizontal:
                isFromChatList && chatTutorialWebState === '4'
                  ? theme.spacing.xs
                  : theme.spacing.m,
            },
          ]}
          accessible={true}
          accessibilityLabel={name ? name : getChatAvatarLabel(chat) || ''}>
          {!collapsed && (
            <Box flexDirection='row' flex={1}>
              {isEditing && (
                <Box justifyContent='center' marginRight='m'>
                  <RadioButton
                    onPress={() => onPress?.(chat)}
                    isSelected={!!isSelected}
                  />
                </Box>
              )}
              {chatAvatar()}

              <Box
                flex={1}
                flexDirection='column'
                justifyContent='center'
                marginLeft='xs'>
                <Box flexDirection='row' justifyContent='space-between'>
                  <Box flex={1}>
                    <Text
                      variant='labelEmphasized'
                      color='textPrimary'
                      numberOfLines={1}
                      accessibilityLabel='Chat title'>
                      {highlightedTitle ? (
                        <Highlighter
                          autoEscape
                          highlightStyle={{
                            color: theme.colors.black,
                            backgroundColor: theme.colors.yellowBright,
                          }}
                          searchWords={filterVal.split(' ') || ''}
                          textToHighlight={highlightedTitle ?? ''}
                        />
                      ) : (
                        chatTitle
                      )}
                    </Text>
                  </Box>
                  <Box flexDirection='row' alignItems='center' ml='m'>
                    {settingsMute && (
                      <TouchableOpacity
                        accessibilityLabel='Muted'
                        onPress={() => onMutePress?.(chat)}>
                        <Icon
                          name='Mute'
                          color='grey03'
                          width={16}
                          height={16}
                        />
                      </TouchableOpacity>
                    )}
                    {settingsPin && (
                      <TouchableOpacity
                        accessibilityLabel='Pinned'
                        onPress={() => onPinPress?.(chat)}>
                        <Icon
                          name='Pin'
                          color='grey03'
                          width={16}
                          height={16}
                          marginHorizontal='xxs'
                        />
                      </TouchableOpacity>
                    )}
                    {unreadCount > 0 && (
                      <Box
                        width={unreadCount > 9 ? 21 : 16}
                        height={16}
                        backgroundColor={
                          unreadCount > 0 ? 'greenSecondary' : 'grey03'
                        }
                        borderRadius='xs'>
                        <Text
                          accessibilityLabel='Unread count'
                          variant='metadataSecondary'
                          fontWeight='500'
                          color='white'
                          textAlign='center'>
                          {unreadCount > 99 ? 99 : unreadCount}
                        </Text>
                      </Box>
                    )}
                  </Box>
                  {isInvited && (
                    <Box
                      backgroundColor='greenBrand'
                      borderRadius='xxs'
                      paddingHorizontal='xxs'
                      marginRight='xxs'
                      alignSelf='center'>
                      <Text variant='labelTabHeaderText' color='white'>
                        {t('models:chat.invited')}
                      </Text>
                    </Box>
                  )}
                  {(isPendingInviteChat || isChatPendingRequest) && (
                    <Box
                      backgroundColor='grey01'
                      borderRadius='xxs'
                      paddingHorizontal='xxs'
                      alignSelf='center'>
                      <Text variant='labelTabHeaderText' color='grey04'>
                        {t('models:chat.pending')}
                      </Text>
                    </Box>
                  )}
                </Box>
                {!highlightedTitle && (
                  <Box
                    flexDirection='row'
                    justifyContent='space-between'
                    style={contentHeightLimit ? { height: 24 } : {}}>
                    <Box
                      flex={1}
                      flexDirection='row'
                      alignItems='center'
                      pointerEvents={filterVal ? 'box-none' : 'none'}
                      onLayout={(e) => setBodyWidth(e.nativeEvent.layout.width)}
                      accessibilityLabel='Message preview'>
                      {messageIcon && !hideIcon && (
                        <Icon
                          name={messageIcon}
                          variant='s'
                          color='black'
                          marginRight='xxs'
                        />
                      )}
                      {highlightedBody && bodyWidth ? (
                        <Box>
                          <Text
                            style={{ width: bodyWidth }}
                            variant='bodySecondary'
                            color='textSecondary'
                            numberOfLines={3}
                            marginRight='l'
                            paddingTop='xxxs'>
                            <Highlighter
                              autoEscape
                              highlightStyle={{
                                color: theme.colors.black,
                                backgroundColor: theme.colors.yellowBright,
                              }}
                              searchWords={filterVal.split(' ') || ''}
                              textToHighlight={
                                getMessageBody(messageBody) ?? ''
                              }
                            />
                          </Text>
                          {messageContent}
                        </Box>
                      ) : (
                        <Text
                          variant='bodySecondary'
                          color={chatSubtitleColor}
                          numberOfLines={1}
                          marginRight='l'
                          paddingTop='xxxs'
                          style={chatSubtitleStyle}>
                          {chatSubtitleContent}
                        </Text>
                      )}
                    </Box>

                    <Box marginLeft='xxs'>
                      <Text variant='metadataSecondary' color='grey04'>
                        {t('timeAgoChatList', {
                          val:
                            recentActivityAt > new Date()
                              ? new Date()
                              : recentActivityAt,
                        })}
                      </Text>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {collapsed && (
            <Box flexDirection='row' flex={1} height={48}>
              {isPendingInviteChat ? (
                <EmailAvatar width={48} />
              ) : (
                <Avatar
                  avatarType={selfChatAvatarType}
                  id={selfChatAvatarId}
                  avatarUrl={chat.avatarUrl}
                  label={name ? name : getChatAvatarLabel(chat)}
                  backgroundColor={
                    isChatPendingRequest ? theme.colors.grey02 : undefined
                  }
                  skipCache={true}
                  isMultiple={isGroupChat}
                  disableAvatarPress
                  isSenderFriendRequest={
                    isChatPendingRequest ? false : isSenderFriendRequest
                  }
                  groupColorSeed={isGroupChat ? name : undefined}
                />
              )}
              {unreadCount > 0 && (
                <Box
                  width={unreadCount > 9 ? 21 : 16}
                  height={16}
                  backgroundColor={
                    unreadCount > 0 ? 'greenSecondary' : 'grey03'
                  }
                  borderRadius='xs'
                  position='absolute'
                  right={0}
                  top={0}>
                  <Text
                    variant='metadataSecondary'
                    fontWeight='500'
                    color='white'
                    textAlign='center'>
                    {unreadCount > 99 ? 99 : unreadCount}
                  </Text>
                </Box>
              )}
              {isHovered && (
                <Box
                  bg='grey05'
                  borderRadius='xxs'
                  width='70px'
                  height='20px'
                  style={{ marginLeft: '-10px', marginBottom: '-8px' }}
                  position='absolute'
                  bottom={0}
                  left={0}
                  alignItems='center'
                  justifyContent='center'
                  px='xxs'
                  mx='xxs'>
                  <Text
                    variant='metadataSecondary'
                    fontWeight='500'
                    color='grey01'
                    numberOfLines={1}>
                    {name ? name : getChatAvatarLabel(chat) || ''}
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </TouchableOpacity>
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  chatCard: {
    flexDirection: 'row',
    ...Platform.select({
      web: {
        paddingVertical: 10,
        paddingHorizontal: theme.spacing.m,
      },
      default: {
        paddingTop: theme.spacing.m,
        paddingBottom: theme.spacing.xs,
        paddingHorizontal: theme.spacing.m,
      },
    }),
  },
});

export default ChatCard;
